import './CampaignsPage.less'

import { ButtonPrimary, HStack, Select } from 'front-end/src/components'
import { breakpoints } from 'front-end/src/components/chakraThemeExtension'
import { IconPlus } from 'front-end/src/components/common/icon/IconPlus'
import {
  CampaignModelAsAdminQueries,
  CampaignScheduleEnum,
  CampaignsSortableColumnsEnum,
  CampaignStateEnum,
  OrderDescriptorCampaigns,
  SqlOrderDirectionEnum
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import IconEmptyCampaigns from 'public/assets/img/empty-campaigns.svg'
import React, { FC, useEffect, useState } from 'react'
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault
} from 'use-query-params'

import { css } from '@emotion/react'
import { t, Trans } from '@lingui/macro'

import { linkBuilder } from 'shared/linkBuilder'
import { navigateTo } from '../../app/util/appHistory'
import ResettableInput from '../../components/common/form/input/ResettableInput'
import { ItemList } from '../../components/common/list/ItemList'
import PageHeader from '../../components/common/page/header/PageHeader'
import { Page } from '../../components/common/page/Page'
import CampaignListItem from '../manage-campaign-page/components/campaign-list-item/CampaignListItem'
import { useCampaignsListQuery } from './CampaignsPage.codegen'
import CreateCampaignModal from './create-campaign-modal/CreateCampaignModal'
import { DEFAULT_PAGE_LIMIT } from 'front-end/src/components/common/table-extended/TableExtended'

const orderOptions = [
  {
    value: `${CampaignsSortableColumnsEnum.created_at}.${SqlOrderDirectionEnum.DESC}`,
    label: <Trans>Date created descending</Trans>
  },
  {
    value: `${CampaignsSortableColumnsEnum.created_at}.${SqlOrderDirectionEnum.ASC}`,
    label: <Trans>Date created ascending</Trans>
  },
  {
    value: `${CampaignsSortableColumnsEnum.name}.${SqlOrderDirectionEnum.ASC}`,
    label: <Trans>Name ascending</Trans>
  },
  {
    value: `${CampaignsSortableColumnsEnum.name}.${SqlOrderDirectionEnum.DESC}`,
    label: <Trans>Name descending</Trans>
  }
]

type Option = {
  label: string
  value: string
}

const getStatusFilterLabel = (option: CampaignStateEnum) => {
  switch (option) {
    case CampaignStateEnum.DEACTIVATED:
      return t`Closed`
    case CampaignStateEnum.DRAFT:
      return t`Inactive`
    case CampaignStateEnum.ACTIVE:
      return t`Active`
    default:
      const exhaustiveCheck: never = option
      throw new Error(`Unhandled case: ${exhaustiveCheck}`)
  }
}

const getScheduleTypeFilterLabel = (option: CampaignScheduleEnum) => {
  switch (option) {
    case CampaignScheduleEnum.FIXED:
      return t`Fixed`
    case CampaignScheduleEnum.IMMEDIATE:
      return t`Immediate`
    case CampaignScheduleEnum.RELATIVE:
      return t`Relative`
    default:
      const exhaustiveCheck: never = option
      throw new Error(`Unhandled case: ${exhaustiveCheck}`)
  }
}

function getFilterOptions(options, getLabel): Option[] {
  return [{ value: null as any, label: t`All` }].concat(
    Object.keys(options).map((value) => {
      return {
        label: getLabel(value),
        value: value as string as typeof options
      }
    })
  )
}

export const CampaignsPage: FC = () => {
  const statusFilterOptions = getFilterOptions(
    CampaignStateEnum,
    getStatusFilterLabel
  )
  const scheduleTypeFilterOptions = getFilterOptions(
    CampaignScheduleEnum,
    getScheduleTypeFilterLabel
  )
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false)
  const [query, setQuery] = useQueryParams({
    status: withDefault(StringParam, null),
    scheduleType: withDefault(StringParam, null),
    search: withDefault(StringParam, null),
    order: withDefault(StringParam, orderOptions[0].value),
    page: withDefault(NumberParam, 1)
  })

  const { data, loading } = useCampaignsListQuery({
    variables: {
      state: query.status as CampaignStateEnum,
      scheduleType: query.scheduleType as CampaignScheduleEnum,
      order: {
        name: query.order.split('.')[0],
        sort: query.order.split('.')[1]
      } as OrderDescriptorCampaigns,
      search: query.search,
      limit: DEFAULT_PAGE_LIMIT,
      offset: (query.page - 1) * DEFAULT_PAGE_LIMIT
    }
  })

  const campaigns = data?.admin?.campaigns?.items
  const campaignsCount = data?.admin?.campaigns?.count

  useEffect(() => {
    if (campaignsCount === 0) {
      setQuery({
        status: null
      })
    }
  }, [campaignsCount])

  return (
    <Page className="CampaignsPage">
      <PageHeader
        className="PageHeader"
        title={t`Campaigns`}
        showBackButton={false}
      />

      <div className="Campaigns__list">
        <ItemList
          page={query.page}
          items={campaigns ?? []}
          itemsCount={campaignsCount ?? 0}
          loading={loading}
          pageSize={DEFAULT_PAGE_LIMIT}
          header={
            <HStack
              align="stretch"
              flexGrow={1}
              p="0 0 16px"
              justifyContent="space-between"
            >
              <HStack spacing="8px">
                <Select
                  leftAddon={<Trans>Status</Trans>}
                  className="Select--filter"
                  minWidth={200}
                  options={statusFilterOptions}
                  value={statusFilterOptions.find(
                    ({ value }) => value === query.status
                  )}
                  onChange={(v) => {
                    if (v) {
                      setQuery({ status: v.value, page: 1 })
                    }
                  }}
                  css={css`
                    @media (max-width: ${breakpoints.md}) {
                      display: none;
                    }
                  `}
                  dataTest="SelectFilter"
                />

                <Select
                  leftAddon={<Trans>Type</Trans>}
                  className="Select--filter"
                  minWidth={200}
                  options={scheduleTypeFilterOptions}
                  value={scheduleTypeFilterOptions.find(
                    ({ value }) => value === query.scheduleType
                  )}
                  onChange={(v) => {
                    if (v) {
                      setQuery({ scheduleType: v.value, page: 1 })
                    }
                  }}
                  css={css`
                    @media (max-width: ${breakpoints.md}) {
                      display: none;
                    }
                  `}
                  dataTest="SelectFilter"
                />

                <Select
                  leftAddon={<Trans>Order</Trans>}
                  options={orderOptions}
                  minWidth={200}
                  onChange={(order) => {
                    setQuery({
                      order: order?.value,
                      page: 1
                    })
                  }}
                  value={orderOptions.find(
                    (option) => option.value === query.order
                  )}
                  className="FilterSelect__Select"
                  dataTest="SelectFilter"
                />

                <ResettableInput
                  onChange={(search) => {
                    setQuery({ search, page: 1 })
                  }}
                  placeholder={t`Search`}
                  title={t`Search`}
                  showResetButton={!!query.search}
                  showSearchIcon
                  value={query.search ?? ''}
                  onReset={() => setQuery({ search: '' })}
                />
              </HStack>

              <ButtonPrimary
                ml="16px"
                onClick={() => {
                  setShowCreateModal(true)
                }}
                leftIcon={<IconPlus />}
              >
                <Trans>New campaign</Trans>
              </ButtonPrimary>
            </HStack>
          }
          renderItem={(
            campaign: CampaignModelAsAdminQueries & {
              audienceJoinEventsCount: number
            }
          ) => (
            <CampaignListItem
              key={campaign.id}
              campaignLink={linkBuilder.campaign(campaign.id)}
              name={campaign.name}
              scheduleType={campaign.schedule_type as CampaignScheduleEnum}
              isActive={campaign.is_active}
              status={campaign.status}
              audienceCount={campaign.audienceJoinEventsCount as number}
              usersCompletedCount={campaign.usersCompletedCount as number}
              actionsCount={campaign.actionsCount as number}
            />
          )}
          onPageChange={(page) => {
            setQuery({ ...query, page })
          }}
        />
      </div>

      {campaignsCount === 0 && (
        <div className="Campaigns__empty no-items">
          <img src={IconEmptyCampaigns} alt={t`Create a new campaign`} />
          <h2>
            <Trans>Create a new campaign</Trans>
          </h2>
          <p>
            <Trans>
              Hit your targets with an automated engagement campaign!
            </Trans>
            <br />
            <Trans>Click the ‘Create a campaign’ button above to start.</Trans>
          </p>
        </div>
      )}

      {showCreateModal && (
        <CreateCampaignModal
          onHide={() => {
            setShowCreateModal(false)
          }}
          onCreate={(id: number) => {
            setShowCreateModal(false)
            navigateTo(linkBuilder.campaignAudience(id))
          }}
        />
      )}
    </Page>
  )
}
